import { validate as isUuid } from 'uuid'
import * as requests from '../api-request'
import getToken from '../get-token'

export const upsertInfo = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/info', data, token)
}

export const exportFdx = async (data = {}, token) => {
  token = token || (await getToken())
  return requests.toBlob('POST', 'script/fdx', data, token)
}

export const upsertCharacters = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/characters', data, token)
}

export const getCharacters = async (data = {}) => {
  const queryString = requests.createQueryString(data)
  return requests.toJSON('GET', `script/characters?${queryString}`)
}

export const upsertScriptLines = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/line_item', data, token)
}

export const upsertScriptLinesV2 = async (data = {}, token) => {
  token = token || (await getToken())
  // A missing token used to resolve `undefined` here, which the callers read
  // as success — the content write just vanished. Fail loudly instead so the
  // autosave retry/failure reporting can react.
  if (!token) {
    throw new Error('Token is required')
  }
  return requests.toJSON('POST', 'script/save_line_items', data, token)
}

export const getScriptsList = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = requests.createQueryString(data).toString()
  const endpoint = queryString ? `script/list?${queryString}` : 'script/list'
  return requests.toJSON('GET', endpoint, {}, token)
}

export const getAllScripts = async (data = {}) => {
  const queryString = requests.createQueryString(data).toString()
  const endpoint = queryString ? `script/list_all?${queryString}` : 'script/list_all'
  return requests.toJSON('GET', endpoint, {})
}

export const getScriptsData = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  // drop malformed identifiers (e.g. null or the string "null") so they
  // never reach the backend; skip the request entirely if none remain
  const scriptUuids = (data.scriptUuids || []).filter(isUuid)
  if (!scriptUuids.length) {
    return []
  }
  return requests.toJSON('POST', 'script/scripts_data', { ...data, scriptUuids }, token)
}

export const getScriptInfo = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = requests.createQueryString(data)
  return requests.toJSON('GET', `script/info?${queryString}`, { }, token)
}

// Lean single-script content read backing services/script-content.js. Unlike
// getScriptsData this does no per-comment user lookup, so it is cheap enough to
// be the read path for every feature page.
export const getScriptContent = async (data = {}, token) => {
  token = token || (await getToken())
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = requests.createQueryString(data)
  return requests.toJSON('GET', `script/content?${queryString}`, {}, token)
}

export const getSnapshotCheckpoint = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = requests.createQueryString(data)
  return requests.toJSON('GET', `script/snapshot_checkpoint?${queryString}`, {}, token)
}

export const saveSnapshotCheckpoint = async (data, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'script/snapshot_checkpoint', data, token)
}

export const saveSnapshot = async (data, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'script/snapshot', data, token)
}

export const deleteScript = async (data, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'script/delete', data, token)
}

export const deleteCharacter = async (data, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  return requests.toJSON('POST', 'script/delete_character', data, token)
}

export const getSnapshots = async (data, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = requests.createQueryString(data)
  return requests.toJSON('GET', `script/snapshot?${queryString}`, data, token)
}

export const getSnapshotDetail = async (data, token, signal) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    throw Error('Token is required')
  }
  const queryString = requests.createQueryString(data)
  return requests.toJSON('GET', `script/snapshot/detail?${queryString}`, {}, token, signal)
}

export const upsertComments = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/comments', data, token)
}

export const deleteComment = async (data = {}, token) => {
  token = token || (await getToken())
  // skip store data if there is no token provided
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/delete_comment', data, token)
}

export const getSharedScripts = async (token) => {
  token = token || (await getToken())
  if (!token) {
    return
  }
  return requests.toJSON('GET', 'script/get_shared_scripts', {}, token)
}

export const getScriptEvents = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    return
  }
  const queryString = new URLSearchParams(data)
  return requests.toJSON('GET', `script/get_script_events?${queryString}`, data, token)
}

export const deleteScriptEvents = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/delete_script_events', data, token)
}

export const getScriptComments = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/get_script_comments', data, token)
}

export const getScripAllComments = async (data) => {
  const querySrinng = requests.createQueryString(data)
  return requests.toJSON('GET', `script/get_script_all_comments?${querySrinng}`, data)
}

export const getScriptSharedInfo = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    return
  }
  const queryString = new URLSearchParams(data)
  return requests.toJSON('GET', `script/get_script_share_info?${queryString}`, data, token)
}

export const updateSynopsis = async (data, token) => {
  token = token || (await getToken())
  if (!token) {
    return
  }
  return requests.toJSON('POST', 'script/update_synopsis', data, token)
}

export const fountainToNolan = async (data) => {
  return requests.toJSON('POST', 'script/fountain_to_nolan', data)
}

export const getOngoingTreatments = async () => {
  return requests.toJSON('GET', 'script/treatment/ongoing')
}

export const checkTreatment = async (data) => {
  const queryString = new URLSearchParams(data)
  return requests.toJSON('GET', `script/treatment/check?${queryString}`)
}

export const resetTreatment = async (data) => {
  return requests.toJSON('POST', 'script/treatment/delete', data)
}

export const getOngoingPilotTreatments = async () => {
  return requests.toJSON('GET', 'script/treatment/pilot/ongoing')
}

export const checkPilotTreatment = async (data) => {
  const queryString = new URLSearchParams(data)
  return requests.toJSON('GET', `script/treatment/pilot/check?${queryString}`)
}

export const resetPilotTreatment = async (data) => {
  return requests.toJSON('POST', 'script/treatment/pilot/delete', data)
}

export const coPilotHistory = async (data) => {
  return requests.toJSON('POST', 'script/co_pilot_history', data)
}
